import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import type { Me } from "../api/client";
import { useMe } from "../api/queries";
import { useLocale } from "../i18n/provider";
import { authClient } from "./client";

interface AuthContextValue {
  me: Me | undefined;
  isLoading: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data: me, isLoading, refetch } = useMe();
  const client = useQueryClient();
  const { setLocale } = useLocale();
  const syncedUser = useRef<string | null>(null);

  useEffect(() => {
    if (!me || syncedUser.current === me.id) return;
    syncedUser.current = me.id;
    setLocale(me.locale);
  }, [me, setLocale]);

  const signOut = useCallback(async () => {
    await authClient.signOut();
    syncedUser.current = null;
    client.clear();
    await client.invalidateQueries({ queryKey: ["me"] });
  }, [client]);

  const refresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const value = useMemo<AuthContextValue>(
    () => ({
      me,
      isLoading,
      isAuthenticated: Boolean(me),
      signOut,
      refresh,
    }),
    [me, isLoading, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
